import { useEffect, useRef, useState } from 'react';
import { draftField, useDraft } from '../../offline/draft.js';
import { Phone, X } from 'lucide-react';
import { INCIDENT_TYPES, STRIKE_SERVICES } from '@nu/shared';
import PhotoPicker from '../../components/PhotoPicker.jsx';
import { reportIncident } from './incidents.js';

const telOf = (p) => `tel:${String(p).replace(/[^\d+]/g, '')}`;

// A question's heading: its number (none for a follow-up), the words, and
// whether it must be answered.
function Q({ n, children, optional = false }) {
  return (
    <span className="q-head">
      {n ? <span className="q-n" aria-hidden="true">{n}</span> : <span className="q-n q-sub" aria-hidden="true" />}
      <span>{children} {optional ? <span className="muted">(optional)</span> : <span className="req">(required)</span>}</span>
    </span>
  );
}

function Choice({ n, label, options, value, onChange }) {
  return (
    <div className="popup-field">
      <Q n={n}>{label}</Q>
      <div className="reason-chips left" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value;
          const l = typeof o === 'string' ? o : o.label;
          return <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={`chip${value === v ? ' on' : ''}`} onClick={() => onChange(v)}>{l}</button>;
        })}
      </div>
    </div>
  );
}

const YES_NO = [{ value: true, label: 'Yes' }, { value: false, label: 'No' }];

// Report an incident on a job: what happened, is anyone hurt, the details,
// photos, and can the job carry on. The team's supervisor (from Match-Ups)
// and operations are alerted at once; the supervisor decides what happens
// next. If someone is hurt, the pop-up says to call the supervisor now.
// onSent(canContinue) lets the job page offer Abort when it can't.
export default function ReportIncident({ job, onClose, onSent }) {
  const ref = useRef(null);
  // The answers are kept on the phone until it's sent or cancelled (photos
  // aren't), so closing it by mistake or the app restarting loses nothing.
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.incident.${job.id}`);
  const [type, setType] = draftField(draft, saveDraft, 'type', '');
  const [service, setService] = draftField(draft, saveDraft, 'service', '');
  const [hurt, setHurt] = draftField(draft, saveDraft, 'hurt', null);
  const [details, setDetails] = draftField(draft, saveDraft, 'details', '');
  const [canContinue, setCanContinue] = draftField(draft, saveDraft, 'canContinue', null);
  const [photos, setPhotos] = useState([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sup = job.supervisor;
  const ready = type && (type !== 'Utility strike' || service) && hurt !== null && details.trim().length >= 3 && canContinue !== null && !adding;
  const send = async () => {
    await reportIncident(job, { type, service: type === 'Utility strike' ? service : null, hurt, details: details.trim(), canContinue }, photos.map((p) => p.blob));
    clearDraft();
    onSent?.({ canContinue, type });
    onClose();
  };

  return (
    <div className="popup-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="popup popup-form" role="dialog" aria-modal="true" aria-labelledby="incident-title" tabIndex={-1} ref={ref}>
        <button type="button" className="popup-x" aria-label="Close" onClick={onClose}><X size={20} aria-hidden="true" /></button>
        <p className="popup-kicker">{job.reference} · {[job.addressLine1, job.postcode].filter(Boolean).join(', ')}</p>
        <h2 id="incident-title" className="popup-title">Report an incident</h2>

        <hr className="q-rule" />
        <Choice n={1} label="What happened?" options={INCIDENT_TYPES} value={type} onChange={setType} />
        {type === 'Utility strike' && <Choice label="Which service was hit?" options={STRIKE_SERVICES} value={service} onChange={setService} />}

        <hr className="q-rule" />
        <Choice n={2} label="Is anyone hurt?" options={YES_NO} value={hurt} onChange={setHurt} />
        {hurt && (
          <div className="incident-hurt" role="alert">
            <strong>Call your supervisor now.</strong> They decide what happens next, including calling 999.
            {sup?.phone
              ? <a className="btn danger-btn popup-btn with-icon" href={telOf(sup.phone)}><Phone size={18} aria-hidden="true" /> Call {sup.name}</a>
              : <span className="small">{sup ? `${sup.name} is alerted when you send this.` : 'Your supervisor is alerted when you send this.'}</span>}
          </div>
        )}

        <hr className="q-rule" />
        <label className="popup-field">
          <Q n={3}>Details</Q>
          <textarea className="popup-input" rows={4} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={4000} placeholder="What happened, where, and what you did to make it safe" />
        </label>

        <hr className="q-rule" />
        <PhotoPicker photos={photos} setPhotos={setPhotos} onBusy={setAdding} stamp={job.reference} label={<Q n={4} optional>Photos</Q>} optional={false} />

        <hr className="q-rule" />
        <Choice n={5} label="Can the job carry on?" options={YES_NO} value={canContinue} onChange={setCanContinue} />

        <hr className="q-rule" />
        {!ready && <p className="small muted">Answer each question to send.</p>}
        <button type="button" className="btn danger-btn popup-btn" disabled={!ready} onClick={send}>Send report</button>
        <p className="small muted popup-sub">{sup ? `${sup.name} (your supervisor)` : 'Your supervisor'} and operations are told straight away. With no signal it sends when there is.</p>
        <button type="button" className="btn popup-btn popup-cancel" onClick={() => { clearDraft(); onClose(); }}>Cancel</button>
      </div>
    </div>
  );
}
